import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileText, Printer } from 'lucide-react';
import { useVehicles } from '../hooks/useVehicles';
import { FORM_GROUPS, buildFormHtml } from '../utils/formTemplates';

// Forms & Checklists: printable sale paperwork (for selling a car) and workshop forms
// (for mechanics and electricians). Pick a car, optionally add names and the price, and open
// any form. It opens in a new tab with a Print / Save PDF button; nothing prints on its own.
// Nothing typed on this page is stored.
export const Forms = () => {
  const { vehicles, loading } = useVehicles();
  const [params] = useSearchParams();
  const [vehicleId, setVehicleId] = useState(params.get('vehicle') || '');
  const [seller, setSeller] = useState('');
  const [buyer, setBuyer] = useState('');
  const [price, setPrice] = useState('');

  const vehicle = useMemo(() => vehicles.find(v => v.id === vehicleId) || null, [vehicles, vehicleId]);

  const open = (id) => {
    const html = buildFormHtml(id, {
      vehicle,
      seller: seller.trim(),
      buyer: buyer.trim(),
      price: price ? Number(price) : null,
      today: new Date().toLocaleDateString('en-GB'),
    });
    if (!html) return;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const win = window.open(url, '_blank');
    if (!win) {
      toast.error('Your browser blocked the new tab. Allow pop-ups for this site and try again.');
      URL.revokeObjectURL(url);
      return;
    }
    // Give the new tab time to load the page before releasing it.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Forms &amp; Checklists</div>
          <div className="page-subtitle">Printable sale paperwork and workshop forms. Open one, then print it or save it as a PDF.</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Fill in the basics (optional)</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
          Pick a car and its details are filled in for you. Anything you type here goes onto the form only and is not saved.
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Vehicle</label>
            <select className="form-input" value={vehicleId} onChange={e => setVehicleId(e.target.value)}>
              <option value="">Leave blank</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number} — {v.brand} {v.model}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Seller name</label>
            <input className="form-input" value={seller} onChange={e => setSeller(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Buyer name</label>
            <input className="form-input" value={buyer} onChange={e => setBuyer(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Sale price (€)</label>
            <input className="form-input" type="number" min="0" step="1" value={price} onChange={e => setPrice(e.target.value)} />
          </div>
        </div>
      </div>

      {FORM_GROUPS.map(group => (
        <div key={group.key} style={{ marginBottom: 28 }}>
          <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 2 }}>{group.title}</div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>{group.blurb}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {group.forms.map(form => (
              <div key={form.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: '#4f8ef7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={16} style={{ color: 'white' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{form.title}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 2, lineHeight: 1.5 }}>{form.desc}</div>
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start', marginTop: 'auto' }} onClick={() => open(form.id)}>
                  <Printer size={13} /> Open to print / save PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, maxWidth: 760 }}>
        These are general-purpose templates and are not legal advice. Many countries also require an official transfer form
        from the registration authority, which you will need to get from them. Tip: after a mechanic signs a job card,
        photograph or scan it and add it to the car with Scan Document so it stays in the car&rsquo;s history.
      </div>
    </div>
  );
};
